import traverson from "traverson";

import { DAY } from "commons/src/NuDateUtils";
import { nucleus } from "commons/src/NucleusTheme";
import { imageBlobToBase64 } from "commons/src/styled/StyledHelper";
import { EFFECTIVE_EVENT as DEFAULT_DATE_TYPE } from "commons/src/constants/dateConstants";

import { EXCLUDED_TYP_CODES } from "./SignificantTypeCodes";
import { fakeInvestments } from "./TransactionFunctions";

export function fetchAccount(
    accountId,
    startDate,
    endDate,
    dateType = DEFAULT_DATE_TYPE,
    buildFilters,
    accountUpdate,
    cashBalanceUpdate,
    clientUpdate,
    transactionsUpdate,
    adviserUpdate,
    themeUpdate
) {
    traverson
        .from(
            `/nucleus-nuclear/nui/nbs/accounts/ids/${accountId}?dateType=${dateType}` +
                `&dateFrom=${getMomentInIsoFormat(startDate)}` +
                `&dateTo=${getMomentInIsoFormat(endDate, true)}` +
                `&excludedTtypeCodes=${EXCLUDED_TYP_CODES}`
        )
        .jsonHal()
        .getResource((error, response, tra) => {
            if (error) {
                throw error;
            }
            response["accountDetails"] = { regularContributions: [], regularWithdrawals: [] };
            accountUpdate(response);
            fetchAccountDetails(tra, response, accountUpdate);
            fetchCashBalances(response.accountNumber, cashBalanceUpdate);
            fetchClient(response.accountNumber, clientUpdate, adviserUpdate, themeUpdate);
            fetchTransactions(
                response.accountNumber,
                startDate,
                endDate,
                dateType,
                buildFilters,
                transactionsUpdate
            );
        });
}

function fetchAccountDetails(tra, account, accountUpdate) {
    tra.continue()
        .jsonHal()
        .follow("accountDetails")
        .getResource((error, eventsResponse) => {
            if (!error) {
                account["accountDetails"] = eventsResponse;
            }
            accountUpdate(account);
        });
}

export function fetchTransactions(
    accountNumber,
    startDate,
    endDate,
    dateType,
    filterBuilder,
    resultsUpdate
) {
    traverson
        .from(
            `/nucleus-nuclear/nui/nbs/accounts/${accountNumber}/transactionEvents?dateType=${dateType}` +
                `&dateFrom=${getMomentInIsoFormat(startDate)}` +
                `&dateTo=${getMomentInIsoFormat(endDate, true)}` +
                `&excludedTtypeCodes=${EXCLUDED_TYP_CODES}` +
                "&size=250"
        )
        .jsonHal()
        .getResource((error, eventsResponse, travResult) => {
            if (error) {
                resultsUpdate(null, 0);
                if (error.statusCode !== undefined) {
                    throw error;
                }
                return;
            }
            const resp = fakeInvestments(eventsResponse);
            filterBuilder(resp);
            resultsUpdate(resp, resp.page.totalPages);
            lazyLoad(travResult, filterBuilder, resultsUpdate, resp);
        });
}

export function fetchClient(accountNumber, clientUpdate, adviserUpdate, themeUpdate) {
    traverson
        .from(`/nucleus-nuclear/nui/nbs/accounts/${accountNumber}/clients`)
        .jsonHal()
        .getResource((error, clientsResponse) => {
            if (error) {
                throw error;
            }
            clientUpdate(clientsResponse.clients[0]);
            fetchAdviser(clientsResponse.clients[0], adviserUpdate, themeUpdate);
        });
}

export function fetchAdviser(client, adviserUpdate, themeUpdate) {
    traverson
        .from(client._links.adviser.href)
        .jsonHal()
        .getResource((error, adviserResponse, trav) => {
            if (error) {
                throw error;
            }
            adviserUpdate(adviserResponse);
            fetchTheme(trav, themeUpdate);
        });
}

export function canSeeHeadOffice() {
    return fetch("/nucleus-nuclear/nui/nbs/locations/1", {
        credentials: "same-origin",
        method: "GET"
    })
        .then(resp => resp.ok)
        .catch(() => false);
}

export function fetchTheme(trav, themeUpdate) {
    canSeeHeadOffice().then(isHead =>
        isHead ? themeUpdate(nucleus) : getThemeForFirm(trav, themeUpdate)
    );
}

export function getThemeForFirm(trav, themeUpdate) {
    trav.continue()
        .follow("locations")
        .getResource((error, locationResp, locTrav) => {
            locTrav
                .continue()
                .follow("theme")
                .getResource((error, theme) => {
                    if (theme._links.logo) {
                        fetch(theme._links.logo.href, { credentials: "same-origin" })
                            .then(resp => resp.blob())
                            .then(blob => imageBlobToBase64(blob))
                            .then(base64 => themeUpdate({ ...theme, logo: base64 }))
                            .catch(() => themeUpdate(nucleus));
                    } else {
                        themeUpdate(theme);
                    }
                });
        });
}

export function fetchCashBalances(accountId, updateCashBalance) {
    traverson
        .from(`/nucleus-nuclear/nui/nbs/accounts/${accountId}/cashBalances`)
        .jsonHal()
        .getResource((error, document) => {
            if (error) {
                throw error;
            }
            const runningCashBalance = {};
            for (const cashBal in document.cashBalances) {
                const trevId = document.cashBalances[cashBal]["trevId"];
                const balance = document.cashBalances[cashBal]["balance"];
                runningCashBalance[trevId] = balance;
            }
            updateCashBalance(runningCashBalance);
        });
}

export function lazyLoad(trav, filterBuilder, resultsUpdate, currentData) {
    trav.continue()
        .jsonHal()
        .follow("next")
        .getResource((error, document, trav) => {
            if (error) {
                resultsUpdate(null, 0);
                if (error.statusCode !== undefined) {
                    throw error;
                }
                return;
            }
            const resp = fakeInvestments(document);
            filterBuilder(resp);
            Array.prototype.unshift.apply(
                resp._embedded.transactionEventResourceList,
                currentData._embedded.transactionEventResourceList
            );
            resultsUpdate(resp, resp.page.totalPages);
            lazyLoad(trav, filterBuilder, resultsUpdate, resp);
        });
}

export function getMomentInIsoFormat(moment, isEndDate) {
    const utcWithLocalTime = moment.clone().utc(true);
    if (isEndDate) {
        utcWithLocalTime.endOf(DAY);
    }
    return utcWithLocalTime.toJSON();
}
